import { FieldState, FormState } from 'formstate';
import { component } from 'tsdi';

import { computed, makeObservable, observable, reaction } from 'mobx';
import { wrapInFormField } from '@core/forms';
import { injectTSDI } from '@core/tsdi';
import { wrapRequest } from 'wrap-request';
import { autodisposer } from '@core/reactions';
import { required, requiredI18n } from '@components/utils/validators';

import { ChallengeDataStore } from '../data-store';
import type {
    CdnObjectDto,
    ChallengeDetailsSupportDto,
    ChallengePresentationSupportDto
} from '@core/api/ChallengeClient';
import { ImageType } from '@core/api/ChallengeClient';
import { Api } from '@core/api';
import {
    formErrors,
    isFormStateDirty,
    makeFormClean
} from '@components/utils/form';
import { StepStatus } from '../store';
import { bind } from 'lodash-decorators';
import { I18n } from '@core/i18n';
import { I18NText } from '@api/CorporateClient';
import { assertDefined } from '@components/utils/error';

type ChallengePresentationFormType = ReturnType<
    ChallengePresentationStore['generateForm']
>;

@component
export class ChallengePresentationStore {
    constructor() {
        this.form = this.generateForm(
            this.challengeDataStore.challengeConfiguration
        );



        makeObservable(this, {
            form: observable,
            state: observable,
            isActive: observable,
            formResetKey: observable,
            fields: computed,
            errors: computed
        });
    }

    private get challengeClient() {
        return injectTSDI(Api).challengeClient;
    }

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    public form: ChallengePresentationFormType;
    public state: StepStatus = StepStatus.CREATING;
    public isActive: boolean = true;
    public formResetKey: number = 0;

    private generateForm(configuration?: ChallengeDetailsSupportDto) {
        if (!configuration) {
            this.state = StepStatus.CREATING;
            return new FormState({
                name: new FieldState<I18NText>(
                    I18n.textToI18nTextConverter()
                ).validators(requiredI18n()),
                photo: new FieldState<CdnObjectDto | undefined>(
                    undefined
                ).validators(required('Select a photo')),
                description: new FieldState<I18NText>(
                    I18n.textToI18nTextConverter()
                ),
                prizeDescription: new FieldState<I18NText>(
                    I18n.textToI18nTextConverter()
                )
            });
        }
        if (!configuration.presentation?.name) {
            this.state = StepStatus.INCOMPLETE;
        } else {
            this.state = StepStatus.COMPLETED;
        }

        return new FormState({
            name: new FieldState<I18NText>(
                configuration.presentation?.name ||
                    I18n.textToI18nTextConverter(
                        configuration.presentation?.name
                    )
            ).validators(requiredI18n('Marked fields are required')),
            photo: new FieldState<CdnObjectDto | undefined>(
                configuration.presentation?.photo
            ).validators(required('Select a photo')),
            prizeDescription: new FieldState<I18NText>(
                I18n.textToI18nTextConverter(
                    configuration.presentation?.prizeDescription
                )
            )
        });
    }

    public get errors() {
        return formErrors(this.form);
    }

    public get formIsDirty() {
        return isFormStateDirty(this.form.$);
    }
    public get fields() {
        return {
            name: wrapInFormField(this.form.$.name),
            prizeDescription: wrapInFormField(this.form.$.prizeDescription),
            photo: wrapInFormField(this.form.$.photo),
            resetKey: this.formResetKey
        };
    }
    public updateChallengePresentationRequest = wrapRequest(
        async ({
            challengeId,
            data
        }: {
            challengeId: number;
            data: ChallengePresentationSupportDto;
        }) => {
            const result =
                await this.challengeClient?.support.challengeSupportControllerUpdatePresentation(
                    challengeId,
                    data
                );
            this.challengeDataStore.liveChallengeConfiguration = result?.data;
            return result?.data;
        }
    );

    @bind
    public async validatePresentation(onErrorOnly: boolean = false) {
        if (onErrorOnly) {
            if (this.state === StepStatus.ERROR) {
                const validation = await this.form.validate();
                return this.validateAll(validation.hasError);
            }
            this.state = StepStatus.MODIFIED;
            return false;
        }
        const validation = await this.form.validate();
        return this.validateAll(validation.hasError);
    }

    private validateAll(hasError: boolean) {
        if (hasError) {
            this.state = StepStatus.ERROR;
            return true;
        } else if (
            this.challengeDataStore.challengeConfiguration === undefined ||
            this.state === StepStatus.CREATING
        ) {
            this.state = StepStatus.CREATING;
            return false;
        } else if (this.formIsDirty) {
            this.state = StepStatus.MODIFIED;
            return false;
        }
        this.state = StepStatus.COMPLETED;
        return false;
    }

    public generateRequestData(
        form: ChallengePresentationFormType
    ): ChallengePresentationSupportDto {
        assertDefined(form.$.photo.value);
        return {
            name: form.$.name.value,
            prizeDescription: form.$.prizeDescription.value,
            photo: form.$.photo.value
        };
    }

    @bind
    public async updateChallengePresentation() {
        const validation = await this.form.validate();

        if (validation.hasError) {
            this.state = StepStatus.ERROR;
            return false;
        } else if (
            !validation.hasError &&
            this.challengeDataStore.challengeId &&
            this.formIsDirty
        ) {
            this.state = StepStatus.UPDATING;
            await this.updateChallengePresentationRequest.request({
                challengeId: this.challengeDataStore.challengeId,
                data: this.generateRequestData(this.form)
            });
            this.state = StepStatus.COMPLETED;
            makeFormClean(this.form);
            return true;
        }
        return false;
    }

    @bind
    public onCollapsedToggle() {
        this.isActive = !this.isActive;
    }

    @bind
    public openStep() {
        this.isActive = true;
    }

    @bind
    public closeStep() {
        this.isActive = false;
    }



    public imageUploadHandler = async (file: File) => {
        const imageUrl = await this.imageUploadRequest.request([file]);
        this.form.$.photo.onChange(imageUrl);
        this.validatePresentation(true);
    };

    public imageUploadRequest = wrapRequest(async ([file]: [File]) => {
        const result =
            await this.challengeClient?.support.imageSupportControllerUpload(
                { type: ImageType.COMMUNITY_EVENT },
                { file }
            );

        return result?.data as CdnObjectDto;
    });

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    this.form = this.generateForm(configuration);
                }
            )
        ];
    }
}
